<template>
  <div class="diagnosis-detail">
    <div class="page-header">
      <div class="header-content">
        <a-breadcrumb class="custom-breadcrumb">
          <a-breadcrumb-item
            ><router-link to="/mobile-cms/guidelines"
              >Sumber Guidelines</router-link
            ></a-breadcrumb-item
          >
          <a-breadcrumb-item v-if="diagnosis?.category?.guideline">
            <router-link :to="`/mobile-cms/guidelines/${diagnosis.category.guideline.source_id}`">
              {{ diagnosis.category.guideline.source?.name || '...' }}
            </router-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item v-if="diagnosis?.category?.guideline">
            <router-link
              :to="`/mobile-cms/guidelines/structure/${diagnosis.category.guideline.id}`"
            >
              {{ diagnosis.category.guideline.title || '...' }}
            </router-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item>{{ diagnosis?.short_name }}</a-breadcrumb-item>
        </a-breadcrumb>
        <h1 class="short-name">
          {{ diagnosis?.short_name }}
        </h1>
        <h2 class="full-name">
          {{ diagnosis?.full_name }}
        </h2>
      </div>
    </div>

    <div class="content-container" v-if="diagnosis">
      <!-- ICD-10 Section -->
      <div class="card-section">
        <h3 class="section-title">Kode ICD-10</h3>
        <div class="icd-list">
          <a-tag
            v-for="icd in diagnosis?.icd_codes"
            :key="icd.id"
            closable
            @close="removeICD(icd)"
            class="icd-tag"
          >
            {{ icd.code }}
          </a-tag>
          <button class="btn-add-inline" @click="showAddICDModal"><plus-outlined /> TAMBAH</button>
        </div>
      </div>

      <!-- Clinical Pathway File Section -->
      <div class="my-2" v-if="diagnosis?.clinical_pathway_file">
        <p
          class="pdf-link"
          style="cursor: pointer; display: inline-flex; align-items: center; gap: 4px"
          @click="openClinicalPathwayFile(diagnosis.clinical_pathway_file)"
        >
          <file-pdf-outlined class="mr-1" />
          {{ diagnosis.clinical_pathway_file.split('/').slice(-1)[0] }}
        </p>
      </div>

      <!-- Sections Management -->
      <div class="card-section">
        <div class="flex-header mb-4">
          <button class="btn-add" @click="showAddSectionModal">
            <plus-outlined class="mr-2" /> TAMBAH
          </button>
        </div>

        <div class="table-responsive custom-table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th style="width: 50px" class="text-center"></th>
                <th style="width: 200px">Nama Bagian</th>
                <th>Isi</th>
                <th style="width: 250px">Aksi</th>
              </tr>
            </thead>
            <draggable
              :list="diagnosis.sections"
              tag="tbody"
              item-key="id"
              handle=".drag-handle"
              @change="updateOrder"
            >
              <template #item="{ element: section }">
                <tr class="section-row">
                  <td class="drag-handle-cell">
                    <holder-outlined class="drag-handle" />
                  </td>
                  <td class="section-title-cell">
                    {{ section.title }}
                  </td>
                  <td class="section-content-cell">
                    <div
                      class="content-text ql-editor"
                      style="padding: 0"
                      @click="handleSectionContentClick"
                      v-html="section.content"
                    ></div>

                    <div
                      v-if="section.alur_algoritma_klinis && section.alur_algoritma_klinis.length"
                      class="mt-3"
                    >
                      <h4 class="sub-title">Alur Algoritma Klinis</h4>
                      <div class="algorithm-tags">
                        <button
                          v-for="algoritma in section.alur_algoritma_klinis"
                          :key="algoritma.id"
                          class="algorithm-tag-btn"
                          @click="goToAlgoritmaDetail(algoritma.id)"
                        >
                          <span class="algorithm-name">{{ algoritma.nama }}</span>
                          <arrow-right-outlined class="algorithm-link-icon" />
                        </button>
                      </div>
                    </div>

                    <div v-if="getSectionCalculators(section).length" class="mt-3">
                      <h4 class="sub-title">Kalkulator</h4>
                      <div class="calculator-tags">
                        <span
                          v-for="calculator in getSectionCalculators(section)"
                          :key="calculator.id"
                          class="calculator-tag"
                        >
                          {{ getCalculatorLabel(calculator) }}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td class="actions-cell">
                    <div class="action-buttons">
                      <button
                        class="btn-action-pill edit-btn"
                        @click="showEditSectionModal(section)"
                      >
                        <edit-outlined /> UBAH
                      </button>
                      <button
                        class="btn-action-pill delete-btn"
                        @click="confirmDeleteSection(section)"
                      >
                        <delete-outlined /> HAPUS
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </draggable>
          </table>
        </div>
      </div>
    </div>

    <!-- ICD Modal -->
    <a-modal
      title="Tambah Kode ICD-10"
      v-model:visible="icdModalVisible"
      :confirm-loading="modalLoading"
      @cancel="icdModalVisible = false"
      :width="400"
      class="premium-modal"
      :footer="null"
    >
      <a-form :model="icdForm" layout="vertical" ref="icdFormRef" class="custom-form">
        <a-form-item name="code" :rules="[{ required: true, message: 'Kode wajib diisi' }]">
          <template #label>
            <span>Kode ICD-10 <span style="color: #ff4d4f">*</span></span>
          </template>
          <a-input v-model:value="icdForm.code" placeholder="Contoh: I21.9" size="large" />
        </a-form-item>

        <a-form-item class="mb-0 mt-8">
          <div class="d-flex justify-center modal-action-buttons">
            <button
              type="button"
              class="confirm-button"
              :disabled="modalLoading"
              @click="handleICDModalOk"
            >
              <span v-if="modalLoading" class="button-spinner"></span>
              {{ modalLoading ? 'MENYIMPAN...' : 'SIMPAN' }}
            </button>
            <button type="button" class="cancel-button" @click="icdModalVisible = false">
              KEMBALI
            </button>
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- Section Modal -->
    <a-modal
      :title="sectionModalTitle"
      v-model:visible="sectionModalVisible"
      :confirm-loading="modalLoading"
      @cancel="handleCloseModal"
      :width="800"
      class="premium-modal"
      :footer="null"
    >
      <a-form :model="sectionForm" layout="vertical" ref="sectionFormRef" class="custom-form">
        <a-form-item name="title" :rules="[{ required: true, message: 'Nama bagian wajib diisi' }]">
          <template #label>
            <span>Nama Bagian <span style="color: #ff4d4f">*</span></span>
          </template>
          <a-input
            v-model:value="sectionForm.title"
            placeholder="Masukkan nama bagian"
            size="large"
          />
        </a-form-item>
        <a-form-item name="content" :rules="[{ required: true, message: 'Isi seksi wajib diisi' }]">
          <template #label>
            <span>Isi <span style="color: #ff4d4f">*</span></span>
          </template>
          <quill-editor
            v-model:value="sectionForm.content"
            style="height: 300px"
            placeholder="Masukkan konten medis"
            :options="editorOptions"
          />
        </a-form-item>
        <a-form-item name="alur_algoritma_klinis_ids">
          <template #label>
            <div style="display: flex; flex-direction: column">
              <span>Alur Algoritma Klinis</span>
              <div
                class="label-description mb-2"
                style="font-weight: 400; font-size: 13px; color: #6b7280"
              >
                Pilih alur algoritma klinis yang ingin ditampilkan
              </div>
            </div>
          </template>
          <a-select
            v-model:value="sectionForm.alur_algoritma_klinis_ids"
            mode="multiple"
            placeholder="Pilih alur algoritma klinis"
            class="w-full"
            size="large"
            show-search
            option-filter-prop="label"
          >
            <a-select-option
              v-for="algoritma in availableAlurAlgoritmaKlinis"
              :key="algoritma.id"
              :value="algoritma.id"
              :label="algoritma.nama"
            >
              {{ algoritma.nama }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item name="calculator_ids">
          <template #label>
            <div style="display: flex; flex-direction: column">
              <span>Kalkulator</span>
              <div
                class="label-description mb-2"
                style="font-weight: 400; font-size: 13px; color: #6b7280"
              >
                Pilih kalkulator yang ingin ditampilkan
              </div>
            </div>
          </template>
          <a-select
            v-model:value="sectionForm.calculator_ids"
            mode="multiple"
            placeholder="Pilih kalkulator"
            class="w-full"
            size="large"
            show-search
            option-filter-prop="label"
          >
            <a-select-option
              v-for="calculator in availableCalculators"
              :key="calculator.id"
              :value="calculator.id"
              :label="getCalculatorLabel(calculator)"
            >
              {{ getCalculatorLabel(calculator) }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item class="mb-0 mt-8">
          <div class="d-flex justify-center modal-action-buttons">
            <button
              type="button"
              class="confirm-button"
              :disabled="modalLoading"
              @click="handleSectionModalOk"
            >
              <span v-if="modalLoading" class="button-spinner"></span>
              {{ modalLoading ? 'MENYIMPAN...' : 'SIMPAN' }}
            </button>
            <button type="button" class="cancel-button" @click="handleCloseModal">KEMBALI</button>
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- Dynamic Confirmation Modal -->
    <a-modal
      v-model:visible="confirmModalVisible"
      :title="confirmModalTitle"
      :footer="null"
      :closable="true"
      @cancel="confirmModalVisible = false"
      :width="450"
      :z-index="9999"
      class="premium-confirmation-modal"
    >
      <div class="confirm-content text-center">
        <p class="confirm-description">{{ confirmModalDescription }}</p>
        <div class="confirm-footer d-flex confirm-modal-action-buttons mt-8">
          <button class="btn-confirm-cancel" @click="confirmModalVisible = false">KEMBALI</button>
          <button
            class="btn-confirm-ok"
            :class="{ 'btn-danger': confirmModalType.includes('delete') }"
            @click="handleConfirmOk"
          >
            {{ confirmModalOkText }}
          </button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, h, markRaw } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { notification } from 'ant-design-vue'
import { CheckCircleOutlined } from '@ant-design/icons-vue'
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  HolderOutlined,
  ArrowRightOutlined,
  FilePdfOutlined,
} from '@ant-design/icons-vue'
import draggable from 'vuedraggable'
import { quillEditor } from 'vue3-quill'
import { mobileCmsGuidelineAPI } from '@/services/axios/perki/mobileCms'

export default {
  name: 'MobileCmsDiagnosisDetail',
  components: {
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    HolderOutlined,
    ArrowRightOutlined,
    FilePdfOutlined,
    draggable,
    quillEditor,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const diagnosisId = route.params.id

    const diagnosis = ref(null)
    const modalLoading = ref(false)
    const availableAlurAlgoritmaKlinis = ref([])
    const availableCalculators = ref([])

    // ICD State
    const icdModalVisible = ref(false)
    const icdFormRef = ref(null)
    const icdForm = reactive({ code: '' })

    // Section State
    const sectionModalVisible = ref(false)
    const editingSection = ref(null)
    const sectionFormRef = ref(null)
    const sectionForm = reactive({
      title: '',
      content: '',
      alur_algoritma_klinis_ids: [],
      calculator_ids: [],
    })

    // Confirmation Modal State
    const confirmModalTarget = ref(null)
    const confirmModalVisible = ref(false)
    const confirmModalType = ref('') // 'section-save', 'section-edit', 'delete'

    const editorOptions = markRaw({
      theme: 'snow',
      modules: {
        toolbar: {
          container: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
            [{ header: [1, 2, 3, false] }],
            ['link', 'image'],
            ['clean'],
          ],
          handlers: {
            image: function () {
              const input = document.createElement('input')
              input.setAttribute('type', 'file')
              input.setAttribute('accept', 'image/*')
              input.click()
              input.onchange = async () => {
                const file = input.files[0]
                const formData = new FormData()
                formData.append('file', file)
                try {
                  const res = await mobileCmsGuidelineAPI.uploadEditorImage(formData)
                  const url = res.data.data.url
                  const host = process.env.VUE_APP_PERKI_API_URL
                    ? process.env.VUE_APP_PERKI_API_URL.replace('/api/v1', '')
                    : 'http://localhost:3000'
                  const fullUrl = `${host}${url}`
                  const quill = this.quill
                  const range = quill.getSelection()
                  quill.insertEmbed(range.index, 'image', fullUrl)
                } catch (err) {
                  console.error('Upload error:', err)
                  notification.error({ message: 'Error', description: 'Gagal mengunggah gambar.' })
                }
              }
            },
          },
        },
      },
    })

    const sectionModalTitle = computed(() =>
      editingSection.value ? 'Ubah Seksi Medis' : 'Tambah Seksi Medis',
    )

    const confirmModalTitle = computed(() => {
      if (confirmModalType.value.includes('delete')) return 'Hapus data?'
      if (confirmModalType.value.includes('edit')) return 'Ubah data?'
      return 'Yakin isi formulir sudah sesuai?'
    })

    const confirmModalDescription = computed(() => {
      if (confirmModalType.value.includes('delete'))
        return 'Data yang dihapus tidak dapat dikembalikan. Apakah Anda yakin ingin melanjutkan?'
      if (confirmModalType.value.includes('edit'))
        return 'Mohon cek kembali perubahan data sebelum menyimpan formulir.'
      return 'Mohon cek kembali data sebelum menyimpan formulir.'
    })

    const confirmModalOkText = computed(() => {
      if (confirmModalType.value.includes('delete')) return 'YA, HAPUS'
      return 'YA, SIMPAN'
    })

    const showSuccessNotification = (msg) => {
      notification.success({
        message: 'Success',
        description: msg,
        placement: 'topRight',
        class: 'premium-notification',
        duration: 3,
      })
    }

    const loadDiagnosis = async () => {
      const res = await mobileCmsGuidelineAPI.getDiagnosisById(diagnosisId)
      const data = res.data.data
      if (data.sections) {
        data.sections.sort((a, b) => a.sort_order - b.sort_order)
      }
      diagnosis.value = data
    }

    const goToAlgoritmaDetail = (id) => {
      router.push(`/mobile-cms/algoritma-klinis/edit/${id}`)
    }

    const loadAlurAlgoritmaKlinisOptions = async () => {
      const res = await mobileCmsGuidelineAPI.getAlurAlgoritmaKlinisOptions()
      availableAlurAlgoritmaKlinis.value = res.data.data || []
    }

    const loadCalculatorOptions = async () => {
      const res = await mobileCmsGuidelineAPI.getCalculatorOptions()
      availableCalculators.value = res.data.data || []
    }

    const getCalculatorLabel = (calculator) =>
      calculator?.name ||
      calculator?.title ||
      calculator?.calculator_name ||
      calculator?.slug ||
      `Calculator #${calculator?.id}`

    const getSectionCalculatorIds = (section) => {
      if (Array.isArray(section?.calculator_ids)) {
        return [...section.calculator_ids]
      }
      if (Array.isArray(section?.calculators)) {
        return section.calculators.map((calculator) => calculator.id)
      }
      return []
    }

    const getSectionCalculators = (section) => {
      if (Array.isArray(section?.calculators) && section.calculators.length) {
        return section.calculators
      }

      const selectedIds = getSectionCalculatorIds(section)
      if (!selectedIds.length) return []

      return selectedIds
        .map((id) => availableCalculators.value.find((calculator) => calculator.id === id))
        .filter(Boolean)
    }

    const showAddICDModal = () => {
      icdForm.code = ''
      icdModalVisible.value = true
    }

    const handleICDModalOk = async () => {
      try {
        await icdFormRef.value.validate()
        modalLoading.value = true
        await mobileCmsGuidelineAPI.addICDCode(diagnosisId, { code: icdForm.code })
        showSuccessNotification('Konten berhasil ditambahkan.')
        icdModalVisible.value = false
        loadDiagnosis()
      } catch (err) {
        notification.error({ message: 'Error', description: 'Gagal menambahkan kode ICD.' })
      } finally {
        modalLoading.value = false
      }
    }

    const removeICD = async (icd) => {
      try {
        await mobileCmsGuidelineAPI.removeICDCode(icd.id)
        showSuccessNotification('Konten berhasil dihapus.')
        loadDiagnosis()
      } catch (error) {
        notification.error({ message: 'Error', description: 'Gagal menghapus kode ICD.' })
      }
    }

    const showAddSectionModal = () => {
      editingSection.value = null
      sectionForm.title = ''
      sectionForm.content = ''
      sectionForm.alur_algoritma_klinis_ids = []
      sectionForm.calculator_ids = []
      sectionModalVisible.value = true
    }

    const showEditSectionModal = (section) => {
      editingSection.value = section
      sectionForm.title = section.title
      sectionForm.content = section.content
      sectionForm.alur_algoritma_klinis_ids = Array.isArray(section.alur_algoritma_klinis_ids)
        ? [...section.alur_algoritma_klinis_ids]
        : Array.isArray(section.alur_algoritma_klinis)
        ? section.alur_algoritma_klinis.map((algoritma) => algoritma.id)
        : []
      sectionForm.calculator_ids = getSectionCalculatorIds(section)
      sectionModalVisible.value = true
    }
    const handleCloseModal = () => {
      sectionModalVisible.value = false
      sectionFormRef.value?.resetFields()
    }

    const handleSectionModalOk = async () => {
      try {
        await sectionFormRef.value.validate()
        confirmModalType.value = editingSection.value ? 'section-edit' : 'section-save'
        confirmModalVisible.value = true
      } catch (err) {
        console.error('Validation error:', err)
      }
    }

    const handleConfirmOk = async () => {
      const type = confirmModalType.value
      confirmModalVisible.value = false

      if (type === 'section-delete') {
        await deleteSection(confirmModalTarget.value)
        return
      }

      modalLoading.value = true
      try {
        const payload = {
          title: sectionForm.title,
          content: sectionForm.content,
          alur_algoritma_klinis_ids: [...sectionForm.alur_algoritma_klinis_ids],
          calculator_ids: [...sectionForm.calculator_ids],
          sort_order:
            editingSection.value?.sort_order || (diagnosis.value?.sections?.length || 0) + 1,
        }
        if (editingSection.value) {
          await mobileCmsGuidelineAPI.updateSection(editingSection.value.id, payload)
          showSuccessNotification('Konten berhasil diubah.')
        } else {
          await mobileCmsGuidelineAPI.createSection(diagnosisId, {
            ...payload,
            diagnosis_id: diagnosisId,
          })
          showSuccessNotification('Konten berhasil ditambahkan.')
        }
        sectionModalVisible.value = false
        loadDiagnosis()
      } catch (err) {
        console.error('Save section error:', err)
        notification.error({ message: 'Error', description: 'Gagal menyimpan seksi.' })
      } finally {
        modalLoading.value = false
      }
    }

    const confirmDeleteSection = (section) => {
      confirmModalTarget.value = section
      confirmModalType.value = 'section-delete'
      confirmModalVisible.value = true
    }

    const deleteSection = async (section) => {
      try {
        await mobileCmsGuidelineAPI.deleteSection(section.id)
        showSuccessNotification('Konten berhasil dihapus.')
        loadDiagnosis()
      } catch (error) {
        notification.error({ message: 'Error', description: 'Gagal menghapus seksi.' })
      }
    }

    const updateOrder = async () => {
      try {
        const sections = diagnosis.value.sections.map((s, index) => ({
          id: s.id,
          sort_order: index + 1,
        }))

        await mobileCmsGuidelineAPI.reorderSections(diagnosisId, { sections })
        showSuccessNotification('Urutan berhasil diperbarui.')
        await loadDiagnosis()
      } catch (error) {
        console.error('Failed to update order:', error)
        notification.error({ message: 'Error', description: 'Gagal memperbarui urutan.' })
      }
    }

    const isHttpUrl = (url) => /^https?:\/\//i.test(url)

    const isDomainLike = (url) => {
      // Handles domains like google.com, www.google.com/path, foo.bar?id=1
      return /^(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+(?:[/?#].*)?$/i.test(url)
    }

    const normalizeExternalUrl = (href) => {
      const raw = (href || '').trim()
      if (!raw) return null
      if (isHttpUrl(raw)) return raw
      if (isDomainLike(raw)) return `https://${raw}`
      return null
    }

    const resolveUploadedFileUrl = (fileUrl) => {
      const rawUrl = (fileUrl || '').trim()
      if (!rawUrl) return ''
      if (/^https?:\/\//i.test(rawUrl)) return rawUrl

      const baseHost = (
        process.env.VUE_APP_PERKI_API_URL || 'http://localhost:3000/api/v1'
      ).replace(/\/api\/v1\/?$/, '')
      const normalizedPath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`
      return `${baseHost}${normalizedPath}`
    }

    const openClinicalPathwayFile = (fileUrl) => {
      const resolvedUrl = resolveUploadedFileUrl(fileUrl)
      if (!resolvedUrl) {
        notification.error({
          message: 'Error',
          description: 'File clinical pathway tidak tersedia.',
        })
        return
      }
      window.open(resolvedUrl, '_blank', 'noopener,noreferrer')
    }

    const handleSectionContentClick = (event) => {
      const anchor = event.target.closest('a')
      if (!anchor) return

      const externalUrl = normalizeExternalUrl(anchor.getAttribute('href'))
      if (!externalUrl) return

      event.preventDefault()
      window.open(externalUrl, '_blank')
    }

    onMounted(() => {
      loadDiagnosis()
      loadAlurAlgoritmaKlinisOptions()
      loadCalculatorOptions()
    })

    return {
      diagnosis,
      modalLoading,
      icdModalVisible,
      icdFormRef,
      icdForm,
      sectionModalVisible,
      editingSection,
      sectionFormRef,
      sectionForm,
      sectionModalTitle,
      availableAlurAlgoritmaKlinis,
      availableCalculators,
      showAddICDModal,
      handleICDModalOk,
      removeICD,
      showAddSectionModal,
      showEditSectionModal,
      handleSectionModalOk,
      handleConfirmOk,
      confirmDeleteSection,
      deleteSection,
      updateOrder,
      goToAlgoritmaDetail,
      handleCloseModal,
      confirmModalVisible,
      confirmModalTitle,
      confirmModalDescription,
      confirmModalOkText,
      confirmModalType,
      editorOptions,
      handleSectionContentClick,
      openClinicalPathwayFile,
      getCalculatorLabel,
      getSectionCalculators,
    }
  },
}
</script>

<style lang="scss">
/* Global overrides for teleported modal content */
.premium-modal .ant-modal-title {
  font-size: 20px;
  font-weight: 700 !important;
  color: #111827;
}

.premium-confirmation-modal .ant-modal-title {
  font-size: 20px;
  font-weight: 700 !important;
  color: #111827;
}

.ant-modal-header {
  border-bottom: none !important;
  text-align: left;
  padding: 24px 24px 0px 24px !important;
}
</style>

<style lang="scss" scoped>
@import '../../styleViews.scss';

.diagnosis-detail {
  background: #fcfcfc;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 4px;
  }
}

.custom-breadcrumb {
  margin-bottom: 8px;
}

.content-container {
  margin-top: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
}

.icd-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 24px;
}

.icd-tag {
  background: #f3f4f6;
  border: none;
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 600;
  color: #4b5563;
}

.flex-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-add {
  height: 40px;
  padding: 0 24px;
  border-radius: 50px;
  background: #e10f28;
  color: white;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s;
  font-family: 'Barlow', sans-serif !important;

  &:hover {
    background: white;
    color: #e10f28;
  }
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 24px;
}

.section-name {
  font-weight: 700;
  color: #111827;
}

.section-actions {
  display: flex;
  gap: 8px;
}

.btn-action-pill {
  height: 32px;
  padding: 0 16px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  background: white;
  transition: all 0.2s;
}

.edit-btn {
  border: 2px solid #282828;
  color: #282828;

  &:hover {
    border-color: #9ca3af;
    background: #f9fafb;
  }
}

.delete-btn {
  background: #1f2937;
  border: 1px solid #1f2937;
  color: white;

  &:hover {
    background: #374151;
    border-color: #374151;
  }
}

.sub-title {
  font-size: 14px;
  font-weight: 700;
  color: #4b5563;
  margin-bottom: 8px;
}

.pdf-link {
  transition: all 0.2s ease-in-out;
  color: $primary;
  font-weight: 700;
  text-decoration: underline;
  &:hover {
    color: $black;
    cursor: pointer;
  }
}

.inner-table :deep(.ant-table-thead > tr > th) {
  background: #f9fafb;
  font-size: 12px;
  padding: 12px 16px;
}

.algorithm-name {
  font-weight: 500;
  font-size: 14px;
}

.btn-add-inline {
  color: #dc2626;
  background: transparent;
  border: none;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
}

.short-name {
  font-size: 30px;
  font-weight: bold;
  @media (max-width: 768px) {
    font-size: 24px;
  }
}

.full-name {
  font-size: 20px;
  font-weight: bold;
  @media (max-width: 768px) {
    font-size: 16px;
  }
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.custom-table-wrapper {
  margin-top: 16px;
  border-radius: 8px;
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  thead {
    tr {
      th {
        background: #f2f4f8 !important;
        font-weight: 600;
        color: #262626;
        padding: 16px 24px;
        border-bottom: 1px solid #e5e7eb;
        text-align: left;
      }
    }
  }

  tbody {
    .section-row {
      border-bottom: 1px solid #f3f4f6;
      transition: background-color 0.2s;
      background: #ffffff;

      &:hover {
        background-color: #f5f5f5;
      }

      td {
        padding: 16px 24px;
        vertical-align: middle;
      }

      .drag-handle-cell {
        color: #9ca3af;
        cursor: grab;
        text-align: center;

        &:active {
          cursor: grabbing;
        }

        .drag-handle {
          font-size: 18px;
        }
      }

      .section-title-cell {
        font-weight: 600;
        color: #282828;
        font-size: 14px;
      }

      .section-content-cell {
        color: #4b5563;
        font-size: 14px;
        line-height: 1.6;
        max-width: 500px;

        :deep(.ql-editor) {
          min-height: auto !important;
          padding: 0 !important;
          white-space: normal;
        }

        :deep(p) {
          margin-bottom: 0 !important;
        }
        margin-bottom: 8px;

        :deep(img) {
          max-width: 100%;
          height: auto;
          max-height: 150px;
          object-fit: contain;
          border-radius: 4px;
          display: block;
          margin-top: 8px;
        }
      }
    }

    .actions-cell {
      min-width: 250px;
      .action-buttons {
        display: flex;
        gap: 12px;
        justify-content: center;
      }
    }
  }
}

.algorithm-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.calculator-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.calculator-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
}

.algorithm-tag-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.18s;
  color: #ea580c;

  &:hover {
    background: #ea580c;
    border-color: #ea580c;
    color: #fff;
  }

  .algorithm-name {
    font-weight: 600;
  }

  .algorithm-link-icon {
    font-size: 10px;
    opacity: 0.8;
  }
}

.text-center {
  text-align: center !important;
}

/* Quill Styles */
:deep(.ql-container) {
  font-family: inherit;
  font-size: 14px;
}

:deep(.ql-editor) {
  min-height: 200px;
}

:deep(.ql-toolbar) {
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  background: #f9fafb;
}

:deep(.ql-container) {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

/* Modal form overrides */
.premium-modal {
  position: relative;
}
.premium-modal :deep(.ant-modal-close-x) {
  font-size: 18px;
}
.custom-form :deep(.ant-form-item) {
  margin-bottom: 24px;
}
.custom-form :deep(label.ant-form-item-required) {
  margin-bottom: 0 !important;
  font-weight: 600;
}
.custom-form :deep(.ant-form-item-label > label) {
  font-weight: 600;
  color: #374151;
}
.custom-form :deep(.ant-form-item-label > label::after),
.custom-form :deep(.ant-form-item-label > label.ant-form-item-required::before),
.custom-form :deep(.ant-form-item-label > label.ant-form-item-required::after) {
  display: none !important;
  content: none !important;
}
.premium-modal :deep(.ant-input),
.premium-modal :deep(.ant-select-selector) {
  border-radius: 8px;
  border-color: #d1d5db;
  font-size: 14px;
}
:deep(.ant-input::placeholder),
:deep(.ant-select-selection-placeholder) {
  color: #798892 !important;
  opacity: 1 !important;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.modal-action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.cancel-button {
  max-width: 230px;
  background: #ffffff;
  border: 2px solid #1a1a1a;
  color: #1a1a1a;
  border-radius: 50px;
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 700;
  font-family: 'Barlow' !important;
  cursor: pointer;
  transition: all 0.3s;
  height: 40px;
  &:hover {
    background: #1a1a1a;
    color: white;
  }
}
.confirm-button {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  max-width: 150px;
  background: #e10f28;
  color: white;
  padding: 6px 16px;
  transition: all 0.3s;
  height: 40px;
  line-height: 1.5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: 'Barlow' !important;
  @media (max-width: 768px) {
    width: 100%;
  }
  &:hover:not(:disabled) {
    background: white;
    border-color: #e10f28;
    color: #e10f28;
  }
  &:disabled {
    border-color: #98a2b3;
    background: #98a2b3;
    color: white;
    cursor: not-allowed;
    opacity: 0.8;
  }
  .button-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid #ffffff;
    border-top: 2px solid transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}
.confirm-description {
  font-size: 16px;
  color: #4b5563;
  text-align: left;
  margin-bottom: 28px;
  padding: 0 !important;
}
.btn-confirm-cancel {
  padding: 8px 20px;
  border-radius: 24px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #111827;
  color: #111827;
  &:hover {
    background: #111827;
    border: 1px solid #111827;
    color: white;
  }
}
.btn-confirm-ok {
  padding: 8px 20px;
  border-radius: 24px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #dc2626;
  color: white;
  border: 1px solid transparent;
  &:hover {
    background: transparent;
    color: #dc2626;
    border: 1px solid #dc2626;
  }
}
.confirm-content {
  text-align: center;
}
.confirm-modal-action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* Notification Styling */
:deep(.premium-notification) {
  border-radius: 12px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);

  .ant-notification-notice-message {
    font-weight: 700;
    margin-bottom: 4px;
  }

  .ant-notification-notice-description {
    font-size: 14px;
    color: #4b5563;
  }

  .ant-notification-notice-close {
    top: 16px;
    right: 16px;
  }
}
</style>
